/**
 * 社区运营域 API（P0 已就绪部分）
 * - 供应商认证审核：kct-user-service，前缀 /api/v1/admin/suppliers/certs
 * - 活动/内容/配置：kct-life-service，前缀 /api/v1/admin/life
 * 依据 doc/管理后台改进方案.md 第五节 & 第八节 8.2
 */
import request, { adaptFlatPage } from './request';

// ============ 供应商认证审核（kct-user-service） ============

// 认证分页列表（status/keyword）
export const getSupplierCerts = async (params) => {
  return request.get('/admin/suppliers/certs', { params });
};

// 认证详情（含营业执照/法人证件临时签名 URL，10 分钟过期）
export const getSupplierCertDetail = async (id) => {
  return request.get(`/admin/suppliers/certs/${id}`);
};

// 审核通过/驳回（结果短信+站内通知供应商）
export const auditSupplierCert = async (id, data) => {
  return request.post(`/admin/suppliers/certs/${id}/audit`, data);
};

// 待审核计数（侧边菜单角标数据源）
export const getSupplierCertPendingCount = async () => {
  return request.get('/admin/suppliers/certs/pending-count');
};

// ============ 活动审核与监管（kct-life-service） ============

// 活动审核列表（auditStatus/page/分类/供应商筛选）
export const getLifeActivities = async (params) => {
  return request.get('/admin/life/activities', { params });
};

// 活动审核：通过（→RECRUITING）/ 驳回（供应商可修改重提）
export const auditLifeActivity = async (id, data) => {
  return request.post(`/admin/life/activities/${id}/audit`, data);
};

// 活动详情（审核查看：图文详情/供应商联系含微信二维码/审核留痕）
// 契约见 doc/后端开发需求文档-活动审核详情接口-管理后台.md；接口未上线时调用方需降级兜底
export const getLifeActivityDetail = async (id) => {
  return request.get(`/admin/life/activities/${id}`);
};

// 活动关闭/强制下架（收费活动触发退款流程；文档 10.1：reason 走 query 参数）
export const closeLifeActivity = async (id, reason) => {
  return request.post(`/admin/life/activities/${id}/close`, null, { params: { reason } });
};

// ============ 内容审核：动态（kct-life-service） ============

// 动态审核列表（auditStatus；增强版支持 type/publisherType/status/activityId/keyword/时间范围筛选，文档 C.3.1）
export const getLifePosts = async (params) => {
  const res = await request.get('/admin/life/posts', { params });
  return adaptFlatPage(res);
};

// 动态详情（含 opLogs 操作审计时间线，文档 C.3.2）
export const getLifePostDetail = async (id) => {
  return request.get(`/admin/life/posts/${id}`);
};

// 删除动态（逻辑删，reason 必填，权限点 life:post:delete，文档 C.3.3）
export const deleteLifePost = async (id, reason) => {
  return request.delete(`/admin/life/posts/${id}`, { params: { reason } });
};

// 动态审核：通过/驳回（结果站内通知作者）
export const auditLifePost = async (id, data) => {
  return request.post(`/admin/life/posts/${id}/audit`, data);
};

// ============ 发布动态（文档 C.2：平台名义/供应商代发，关联活动） ============

// 后台发布动态（发布即 PENDING 待审核；视频上传专员仅限 type=VIDEO + publisherType=PLATFORM）
export const adminPublishPost = async (data) => {
  return request.post('/admin/life/posts', data);
};

// 代发供应商候选（认证通过且账号正常，最多 50 条）
export const getSupplierOptions = async (keyword) => {
  return request.get('/admin/life/posts/supplier-options', { params: keyword ? { keyword } : {} });
};

// 可关联活动候选（RECRUITING/ONGOING；supplierId 为空返回全平台，否则限该供应商名下）
export const getActivityOptions = async ({ supplierId, keyword } = {}) => {
  const params = {};
  if (supplierId) params.supplierId = supplierId;
  if (keyword) params.keyword = keyword;
  return request.get('/admin/life/posts/activity-options', { params });
};

// 管理端图片上传（jpg/png/webp ≤10MB，返回 {url}）
export const adminUploadImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return request.post('/admin/life/files/upload/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// 管理端视频上传（mp4/mov/webm ≤500MB；HEVC 同步转码 H.264，大文件耗时长需放宽超时）
export const adminUploadVideo = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return request.post('/admin/life/files/upload/video', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000, // 10 分钟：覆盖转码耗时（实例默认超时不足）
  });
};

// ============ 供应商消息管理（文档 C.4：先审后发） ============

// 消息分页列表（聚合供应商名称/目标活动标题；扁平分页需修正真实 total）
export const getLifeNotices = async (params) => {
  const res = await request.get('/admin/life/notices', { params });
  return adaptFlatPage(res);
};

// 消息详情（全字段 + opLogs 审计时间线）
export const getLifeNoticeDetail = async (id) => {
  return request.get(`/admin/life/notices/${id}`);
};

// 消息审核（approved/reason 驳回必填；通过后立即发送或进入定时调度）
export const auditLifeNotice = async (id, data) => {
  return request.post(`/admin/life/notices/${id}/audit`, data);
};

// 撤回定时消息（仅 SCHEDULED 可撤回，撤回后回到 DRAFT）
export const revokeLifeNotice = async (id) => {
  return request.post(`/admin/life/notices/${id}/revoke`);
};

// 删除消息（DRAFT/REJECTED 可删；SCHEDULED 先撤回；SENT 返回 4002；reason 必填）
export const deleteLifeNotice = async (id, reason) => {
  return request.delete(`/admin/life/notices/${id}`, { params: { reason } });
};

// 已公开动态下架/上架（文档 10.2：offline 与 reason 走 query 参数）
export const offlineLifePost = async (id, { offline = true, reason } = {}) => {
  return request.post(`/admin/life/posts/${id}/offline`, null, { params: { offline, reason } });
};

// ============ 内容审核：评论（kct-life-service） ============

// 评论列表（默认待复审工作台视图；auditStatus=ALL 查看全量，文档 C.5.1）
export const getLifeComments = async (params) => {
  const res = await request.get('/admin/life/comments', { params });
  return adaptFlatPage(res);
};

// 评论审核：通过/驳回（通过后可见并 commentCount+1）
export const auditLifeComment = async (id, data) => {
  return request.post(`/admin/life/comments/${id}/audit`, data);
};

// 删除评论（逻辑删，可见评论删除后 commentCount-1，权限点 life:comment:delete，文档 C.5.3）
export const deleteLifeComment = async (id, reason) => {
  return request.delete(`/admin/life/comments/${id}`, { params: { reason } });
};

// ============ 平台运营配置：分类/Banner（kct-life-service） ============

// 分类配置（type 可选：ACTIVITY-活动分类 / POST-帖子分类，不传返回全部）
export const getLifeCategories = async (params) => {
  return request.get('/admin/life/categories', { params });
};

export const createLifeCategory = async (data) => {
  return request.post('/admin/life/categories', data);
};

export const updateLifeCategory = async (id, data) => {
  return request.put(`/admin/life/categories/${id}`, data);
};

// 删除分类（活动分类存在引用时后端拒绝并返回引用数量提示）
export const deleteLifeCategory = async (id) => {
  return request.delete(`/admin/life/categories/${id}`);
};

// Banner 配置
export const getLifeBanners = async () => {
  return request.get('/admin/life/banners');
};

export const createLifeBanner = async (data) => {
  return request.post('/admin/life/banners', data);
};

export const updateLifeBanner = async (id, data) => {
  return request.put(`/admin/life/banners/${id}`, data);
};

// ============ 实名认证审核（kct-user-service，文档：前端对接文档-实名认证人工审核-管理后台） ============

// 用户实名认证列表（level L2/L3、status、keyword 模糊匹配姓名/身份证号）
export const getUserCerts = async (params) => {
  return request.get('/admin/user-certs', { params });
};

// 待审核数量（侧边菜单角标 + 页面统计卡数据源，data 为 Long）
export const getUserCertPendingCount = async () => {
  return request.get('/admin/user-certs/pending-count');
};

// 认证详情（完整身份证号 + 证件照预签名 URL 10 分钟过期 + OCR 对比字段）
export const getUserCertDetail = async (certId) => {
  return request.get(`/admin/user-certs/${certId}`);
};

// 人工复审（action: APPROVE/REJECT；REJECT 必填 reason ≤200 字）
export const auditUserCert = async (certId, data) => {
  return request.post(`/admin/user-certs/${certId}/audit`, data);
};

// 管理员手动实名认证（userId/phone 至少一个，userId 优先；直接完成 L2）
export const manualUserCert = async (data) => {
  return request.post('/admin/user-certs/manual', data);
};

// 管理员手动供应商企业认证（目标为供应商账号 userType=6，直接完成企业认证）
export const manualSupplierCert = async (data) => {
  return request.post('/admin/suppliers/certs/manual', data);
};

// ============ 报名监管（P1：⏳ 后端待补） ============

// 全平台报名记录检索（按活动/供应商/状态）
export const getLifeSignups = async (params) => {
  return request.get('/admin/life/signups', { params });
};

// 报名详情
export const getLifeSignupDetail = async (id) => {
  return request.get(`/admin/life/signups/${id}`);
};

// 异常报名干预（取消报名等）
export const interveneLifeSignup = async (id, data) => {
  return request.post(`/admin/life/signups/${id}/intervene`, data);
};

// ============ 活动订单监管（P1：⏳ 后端待补） ============

// 订单列表（待支付/已冻结/已分账/已退款）
export const getLifeOrders = async (params) => {
  return request.get('/admin/life/orders', { params });
};

// 订单详情（含担保流水）
export const getLifeOrderDetail = async (id) => {
  return request.get(`/admin/life/orders/${id}`);
};

// 争议退款人工审批（仲裁委员角色，审批留痕）
export const refundAuditLifeOrder = async (id, data) => {
  return request.post(`/admin/life/orders/${id}/refund-audit`, data);
};

// 手动验收（72h 自动验收的兜底）
export const forceConfirmLifeOrder = async (id, data) => {
  return request.post(`/admin/life/orders/${id}/force-confirm`, data);
};

// ============ 积分与会员配置（P1：⏳ 后端待补） ============

// 积分规则查询（签到/任务/等级阈值，即改即生效缓存刷新）
export const getLifePointsRules = async () => {
  return request.get('/admin/life/points/rules');
};

// 积分规则更新
export const updateLifePointsRules = async (data) => {
  return request.put('/admin/life/points/rules', data);
};

// 积分人工调整（指定用户加减分，必填原因，写审计日志）
export const adjustLifePoints = async (data) => {
  return request.post('/admin/life/points/adjust', data);
};

// 积分调整记录
export const getLifePointsAdjustments = async (params) => {
  return request.get('/admin/life/points/adjustments', { params });
};

// 会员套餐列表
export const getLifeMembershipPlans = async () => {
  return request.get('/admin/life/membership/plans');
};

// 创建会员套餐
export const createLifeMembershipPlan = async (data) => {
  return request.post('/admin/life/membership/plans', data);
};

// 更新会员套餐（价格/时长/权益 JSON/上下架/排序）
export const updateLifeMembershipPlan = async (id, data) => {
  return request.put(`/admin/life/membership/plans/${id}`, data);
};

// 阶梯费率配置（活动类型 × 供应商会员等级 5%-15% 服务费率，仅影响新订单）
export const getLifeRateConfig = async () => {
  return request.get('/admin/life/rate-config');
};

export const updateLifeRateConfig = async (data) => {
  return request.put('/admin/life/rate-config', data);
};

// ============ 运营配置扩展（P1：⏳ 后端待补） ============

// 首页热点（最多 3 条，关联活动/通知/动态）
export const getLifeHotspots = async () => {
  return request.get('/admin/life/home-config/hotspots');
};

export const updateLifeHotspots = async (data) => {
  return request.put('/admin/life/home-config/hotspots', data);
};

// FAQ 分类与问答
export const getLifeFaqs = async (params) => {
  return request.get('/admin/life/help/faqs', { params });
};

export const createLifeFaq = async (data) => {
  return request.post('/admin/life/help/faqs', data);
};

export const updateLifeFaq = async (id, data) => {
  return request.put(`/admin/life/help/faqs/${id}`, data);
};

// 供应商端通知模板
export const getLifeNoticeTemplates = async () => {
  return request.get('/admin/life/notice-templates');
};

export const updateLifeNoticeTemplate = async (id, data) => {
  return request.put(`/admin/life/notice-templates/${id}`, data);
};

// 系统通知发布（向全体/分批用户推送，messageKey=life.system）
export const publishLifeSystemNotice = async (data) => {
  return request.post('/admin/life/notices/system', data);
};

// ============ 资金担保与分账（P1：⏳ 后端待补） ============

// 担保总览（冻结总额/待验收金额/待分账金额）
export const getLifeEscrowOverview = async () => {
  return request.get('/admin/life/escrow/overview');
};

// 担保流水（FREEZE/RELEASE/REFUND/SPLIT，按订单/时间检索）
export const getLifeEscrowFlows = async (params) => {
  return request.get('/admin/life/escrow/flows', { params });
};

// 分账记录（分账金额/服务费/状态，按供应商筛选）
export const getLifeSettlements = async (params) => {
  return request.get('/admin/life/settlements', { params });
};

// ============ 社区数据看板（P1：⏳ M6 排期 R-LIFE-ADM-05） ============

// 看板数据：today 今日指标 / pending 待办计数 / trend 趋势
export const getLifeDashboard = async (params) => {
  return request.get('/admin/life/dashboard', { params });
};
